import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { relTime } from "@/lib/format";
import { toast } from "sonner";
import { Send, Plus, MessageSquare, Sparkles, Trash2 } from "lucide-react";

export default function StrategyAI() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = () =>
    api.get("/strategy/sessions").then(({ data }) => {
      setSessions(data);
      if (!activeId && data.length) setActiveId(data[0]._id);
    });

  useEffect(() => { loadSessions().catch(() => {}); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    api.get(`/strategy/sessions/${activeId}/messages`).then(({ data }) => setMessages(data)).catch(() => {});
  }, [activeId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const newSession = async () => {
    try {
      const { data } = await api.post("/strategy/sessions", { title: "New strategy chat" });
      setSessions((s) => [data, ...s]);
      setActiveId(data._id);
      setMessages([]);
    } catch {
      toast.error("Could not create session");
    }
  };

  const delSession = async (id) => {
    await api.delete(`/strategy/sessions/${id}`).catch(() => {});
    setSessions((s) => s.filter((x) => x._id !== id));
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const content = text.trim();
    if (!content || !activeId) {
      if (!activeId) {
        // auto-create
        try {
          const { data } = await api.post("/strategy/sessions", { title: content.slice(0, 60) || "Chat" });
          setSessions((s) => [data, ...s]);
          setActiveId(data._id);
          _dispatchSend(data._id, content);
        } catch {
          toast.error("Could not start chat");
        }
      }
      return;
    }
    _dispatchSend(activeId, content);
  };

  const _dispatchSend = async (sid, content) => {
    setSending(true);
    setText("");
    // optimistic append
    const optimistic = { _id: `opt-${Date.now()}`, role: "user", content, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      const { data } = await api.post(`/strategy/sessions/${sid}/message`, { content });
      setMessages((m) => [...m.filter((x) => x._id !== optimistic._id), optimistic, data]);
      loadSessions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI failed");
    } finally {
      setSending(false);
    }
  };

  const suggested = [
    "Design a mean-reversion strategy for BTC on a 4-hour timeframe.",
    "What's a good SMA cross setup for TSLA — and how do I tune the windows?",
    "Explain Bollinger Band squeezes and when they signal breakouts.",
    "How should I size positions and place stops for a $10k paper account?",
  ];

  return (
    <div className="space-y-6" data-testid="strategy-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">AI Coach</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-3">
          Strategy builder <Sparkles className="text-gain" size={28} />
        </h1>
        <p className="text-gray-500 mt-2">
          Chat with Claude Sonnet 4.5 to design, refine, and stress-test paper-trading strategies.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 min-h-[560px]">
        {/* Sessions */}
        <Card className="lg:col-span-3 border border-gray-200 rounded-2xl p-4 overflow-hidden flex flex-col max-h-[720px]" data-testid="sessions-list">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Chats</p>
            <button
              onClick={newSession}
              data-testid="new-session-btn"
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-brand-deep"
              title="New chat"
            >
              <Plus size={14} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 space-y-1 pr-1">
            {sessions.length === 0 && (
              <p className="text-xs text-gray-400 px-2 py-4 text-center">No chats yet.</p>
            )}
            {sessions.map((s) => (
              <button
                key={s._id}
                onClick={() => setActiveId(s._id)}
                data-testid={`session-${s._id}`}
                className={`group w-full text-left px-3 py-2 rounded-xl text-sm truncate flex items-center justify-between ${
                  activeId === s._id ? "bg-gray-100 text-black" : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <span className="flex items-center gap-2 truncate">
                  <MessageSquare size={12} className="shrink-0" />
                  <span className="truncate">{s.title}</span>
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    delSession(s._id);
                  }}
                  role="button"
                  tabIndex={0}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-loss"
                >
                  <Trash2 size={12} />
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* Messages */}
        <Card className="lg:col-span-9 border border-gray-200 rounded-2xl p-6 flex flex-col overflow-hidden min-h-[560px] max-h-[720px]" data-testid="chat-panel">
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <Sparkles className="text-gain mb-3" size={32} />
                <p className="font-display text-2xl font-medium">How can I help you trade smarter?</p>
                <p className="text-sm text-gray-500 mt-2">Ask about strategy design, entries, risk, and more.</p>
                <div className="mt-6 grid sm:grid-cols-2 gap-2 max-w-2xl w-full">
                  {suggested.map((q) => (
                    <button
                      key={q}
                      onClick={() => {
                        setText(q);
                      }}
                      data-testid={`suggested-${q.slice(0, 15).replace(/\s/g, "-")}`}
                      className="text-left text-sm p-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m._id}
                data-testid={`msg-${m.role}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 leading-relaxed whitespace-pre-wrap text-sm ${
                    m.role === "user"
                      ? "bg-brand text-white rounded-br-md"
                      : "bg-gray-100 text-black rounded-bl-md"
                  }`}
                >
                  {m.content}
                  <p className={`text-[10px] mt-2 ${m.role === "user" ? "text-gray-300" : "text-gray-500"}`}>
                    {relTime(m.created_at)}
                  </p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-gray-100 rounded-2xl px-4 py-3 rounded-bl-md">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "0.15s" }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "0.3s" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="mt-4 flex items-end gap-2" data-testid="chat-form">
            <Textarea
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Ask about strategies, indicators, position sizing…"
              className="rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand min-h-[56px] max-h-[160px] resize-none"
            />
            <Button
              type="submit"
              data-testid="chat-send"
              disabled={sending || !text.trim()}
              className="h-14 w-14 rounded-full bg-brand text-white hover:bg-brand-deep shrink-0"
            >
              <Send size={16} />
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
